import useTranslation from 'next-translate/useTranslation';
import setLanguage from 'next-translate/setLanguage';

import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';
import classNames from 'classnames';

import { Button, Modal, Input, ErrorModal, ScreenLoader } from '@/ui/index';
import {
  hooks as cabinetHooks,
  selectors as cabinetSelectors,
} from '@/domains/cabinet';
import { langFormatter } from '@/utils/languageFormat';
import formatPhoneNumberNew from '@/utils/formatPhoneNumberNew';
import { OutlineCheck } from '@/components/SvgComponents/OutlineCheck';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import { SuccessModal } from '@/components/ui/Modals/Success/SuccessModal';
import { PaymentStatus } from '@/enums/numberSale';

import styles from './index.module.scss';

export const TopUpModal = ({
  active,
  setTopUpModalActive,
  accounts,
  selectedAccountId,
  data,
  isMainPage = false,
}) => {
  const router = useRouter();
  const { paymentStatus } = router.query;

  const { t, lang } = useTranslation();
  const formattedLang = langFormatter({ lang });
  const numberVariants = {
    EXISTNUMBER: 'EXISTNUMBER',
    OTHERNUMBER: 'OTHERNUMBER',
  };
  const customAmounts = [1, 5, 10];
  const [selectedVariant, setSelectedVariant] = useState(
    numberVariants.EXISTNUMBER,
  );
  const [succesModalActive, setSuccessModalActive] = useState(false);
  const [errorModalActive, setErrorModalActive] = useState(false);
  const [topUpTrigger, { error, isLoading }] =
    cabinetHooks.useTopUpWithoutCard();
  const number = accounts?.find((account) => account?.id === selectedAccountId)
    ?.phone?.number;
  useEffect(() => {
    const lang = sessionStorage.getItem('lang');
    if (lang && lang !== router.locale) {
      setLanguage(lang);
    }
    sessionStorage.removeItem('lang');

    const handleWheel = (event) => {
      if (document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const FormSchema = Yup.object().shape({
    amount: Yup.number()
      .min(1, t('common:AmountMin'))
      .max(250, t('common:AmountWarning'))
      .required(t('common:Required')),
    phone: Yup.string()
      .matches(/^\+994-\d{2}-\d{3}-\d{2}-\d{2}$/, t('common:Required'))
      .required(t('common:Required')),
  });
  const cleanedPhoneNumber = (value) => {
    return value.replace(/[+-]/g, '');
  };

  useEffect(() => {
    if (!router.query.dynamicPage) {
      const statusLower = paymentStatus?.toLowerCase();
      if (statusLower === PaymentStatus.SUCCESS.toLowerCase()) {
        setSuccessModalActive(true);
      } else if (
        Object.values(PaymentStatus).some(
          (status) => status.toLowerCase() === statusLower,
        )
      ) {
        setErrorModalActive(true);
      }
    }
  }, [router.isReady, router.pathname]);

  const formik = useFormik({
    initialValues: {
      amount: '',
      phone: formatPhoneNumber(`994${number}`),
    },
    validationSchema: FormSchema,
    validateOnMount: false,
    onSubmit: async (values) => {
      try {
        const { url } = await topUpTrigger({
          lang: formattedLang,
          amount: Number(values.amount),
          receiver:
            selectedVariant === numberVariants.OTHERNUMBER || isMainPage
              ? cleanedPhoneNumber(values?.phone)
              : cleanedPhoneNumber(number),
        }).unwrap();
        sessionStorage.setItem('lang', lang);
        router.replace(url);
        setTopUpModalActive(false);
      } catch (error) {
        setTopUpModalActive(false);
        setErrorModalActive(true);
      } finally {
        formik.resetForm();
      }
    },
  });
  const topUpModalHandler = () => {
    setTopUpModalActive(false);
    formik.resetForm();
    setSelectedVariant(numberVariants.EXISTNUMBER);
  };

  const handleKeyDown = (e) => {
    const { amount } = formik?.values || {};
    if (
      (amount.length === 0 && e.key === '.') ||
      (amount.length === 1 && e.key === '0' && amount === '0') ||
      ['-', 'e', 'E'].includes(e.key) ||
      (amount.length === 0 && e.key === '0') ||
      (amount.length === 0 && e.key === ',')
    ) {
      e.preventDefault();
    }
  };

  return (
    <>
      <Modal
        className={styles.customTopupModal}
        active={active}
        setActive={topUpModalHandler}
        data-testid={'topup'}
        itemScope
        itemType='https://schema.org/WebPage'
      >
        <h1>{t('cabinet:topUp')}</h1>
        {!isMainPage && (
          <>
            <h5>{t('cabinet:selectAccount')}</h5>

            <div className={styles.selections}>
              <div
                onClick={() => {
                  setSelectedVariant(numberVariants.EXISTNUMBER);
                  formik.setFieldValue(
                    'phone',
                    formatPhoneNumber(`994${number}`),
                  );
                }}
                className={`${styles.existNumber} ${
                  selectedVariant === numberVariants.EXISTNUMBER &&
                  styles.existNumber__active
                }`}
              >
                <div className={styles.top}>
                  <p>{formatPhoneNumberNew(number)}</p>
                  {selectedVariant === numberVariants.EXISTNUMBER && (
                    <OutlineCheck />
                  )}
                </div>
                <div className={styles.bottom}>
                  <p className={styles.title}>{t('cabinet:balance')}</p>
                  <p className={styles.amount}>
                    {data?.mainBalance ?? data?.totalPossibleLimit} ₼
                  </p>
                </div>
              </div>
              <div
                onClick={() => {
                  setSelectedVariant(numberVariants.OTHERNUMBER);
                  formik.setFieldValue('phone', '');
                }}
                className={classNames([
                  styles.otherNumber,
                  {
                    [styles.otherNumber__active]:
                      selectedVariant === numberVariants.OTHERNUMBER,
                  },
                ])}
              >
                <div className={styles.top}>
                  <p>{t('cabinet:otherNumber')}</p>
                  {selectedVariant === numberVariants.OTHERNUMBER && (
                    <OutlineCheck />
                  )}
                </div>
                <div className={styles.bottom}>
                  <p className={styles.title}>{t('cabinet:otherNumberDesc')}</p>
                </div>
              </div>
            </div>
          </>
        )}

        {(selectedVariant === numberVariants.OTHERNUMBER || isMainPage) && (
          <div className={styles.otherNumberInput}>
            <Input
              onBlur={formik.handleBlur('phone')}
              error={formik.touched.phone && formik.errors.phone}
              value={formik.values.phone}
              type='tel'
              inputMode='numeric'
              onChange={(value) => {
                const formattedPhone = formatPhoneNumber(value);
                formik.setFieldValue('phone', formattedPhone);
              }}
              label={t('cabinet:number')}
              placeholder={'+994-XX-XXX-XX-XX'}
              mask={'+\\9\\9\\4-99-999-99-99'}
              data-testid={'topup-phone-input'}
            />
          </div>
        )}
        <h5>{t('cabinet:amount')}</h5>
        <div
          className={`${styles.amountInput} ${
            isMainPage && styles.amountInput__main
          }`}
        >
          <Input
            onBlur={formik.handleBlur('amount')}
            error={formik.touched.amount && formik.errors.amount}
            value={formik.values.amount}
            onChange={(value) => {
              if (value.includes('.') && value.split('.')[1].length > 2) {
                return;
              }
              formik.setFieldValue('amount', value);
            }}
            placeholder={t('cabinet:amountText')}
            type='number'
            inputMode='numeric'
            pattern='[0-9]*'
            onKeyDown={handleKeyDown}
            data-testid={'topup-amount-input'}
          />
          <p>₼</p>
        </div>
        <div className={styles.amountOptions}>
          {customAmounts?.map((amount) => {
            return (
              <span
                key={amount}
                onClick={() => formik.setFieldValue('amount', amount)}
              >
                {amount} ₼
              </span>
            );
          })}
        </div>
        <div className={styles.redLine}></div>
        <div>
          <Button
            data-testid={'topup-submit'}
            onClick={formik.handleSubmit}
            disabled={!formik.isValid}
          >
            {t('common:Continue')}
          </Button>
        </div>
      </Modal>
      <ErrorModal
        active={errorModalActive}
        setActive={() => {
          const { pathname } = router;
          router.replace({ pathname }, undefined, { shallow: true });
          setErrorModalActive(false);
        }}
        itemScope
        itemType='https://schema.org/WebPage'
      >
        {error?.message || t('common:FailMessage')}
      </ErrorModal>
      <SuccessModal
        active={succesModalActive}
        setSuccessModal={() => {
          const { pathname } = router;
          router.replace({ pathname }, undefined, { shallow: true });
          setSuccessModalActive(false);
        }}
      ></SuccessModal>
      <ScreenLoader visible={isLoading} />
    </>
  );
};
